import { useState, useEffect } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loader2, Check, Plus, Tag } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TagDocumentsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentIds: string[]
}

export function TagDocumentsDialog({
  open,
  onOpenChange,
  documentIds,
}: TagDocumentsDialogProps) {
  const {
    documents,
    tags,
    fetchTags,
    createTag,
    addTagToDocument,
    removeTagFromDocument,
  } = useLibraryStore()

  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set())
  const [newTagName, setNewTagName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const isSingleDocument = documentIds.length === 1
  const document = isSingleDocument
    ? documents.find((d) => d.id === documentIds[0])
    : null
  const documentName = document?.name

  // Stable key identifying which documents this dialog targets. Used as the
  // effect key so re-initialization happens on open (or when reopened for a
  // different document), independent of the documents array's identity.
  const documentKey = documentIds.join(',')

  // Initialize selection + input ONLY on the open transition (or when targeting
  // a different document). Deliberately NOT keyed on `document`/`tags.length`:
  // `document` is `documents.find(...)`, a fresh object every time the store's
  // documents array is replaced — and status polling replaces it every 3s while
  // any doc is processing (library-store startStatusPolling). Keying on those
  // re-ran this initializer on each background refresh and wiped the new-tag
  // name mid-typing. `document`/`tags` are read from the open-transition
  // closure, which is the correct snapshot. See TagDocumentsDialog.test.tsx.
  useEffect(() => {
    if (!open) return

    // Ensure tags are loaded (available-tags list reads `tags` in render).
    if (tags.length === 0) {
      void fetchTags()
    }

    // For single document, pre-select its current tags; bulk starts empty.
    if (isSingleDocument && document) {
      setSelectedTagIds(new Set(document.tags.map((t) => t.id)))
    } else {
      setSelectedTagIds(new Set())
    }
    setNewTagName('')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/documentKey only; see comment above
  }, [open, documentKey])

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev)
      if (next.has(tagId)) {
        next.delete(tagId)
      } else {
        next.add(tagId)
      }
      return next
    })
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return

    setIsCreating(true)
    try {
      const newTag = await createTag(newTagName.trim())
      setSelectedTagIds((prev) => new Set([...prev, newTag.id]))
      setNewTagName('')
    } catch {
      // Error handled in store
    } finally {
      setIsCreating(false)
    }
  }

  const handleApply = async () => {
    setIsSaving(true)
    try {
      for (const docId of documentIds) {
        const doc = documents.find((d) => d.id === docId)
        const currentTagIds = new Set(doc?.tags.map((t) => t.id) ?? [])

        // Add new tags
        for (const tagId of selectedTagIds) {
          if (!currentTagIds.has(tagId)) {
            await addTagToDocument(docId, tagId)
          }
        }

        // Remove deselected tags (single doc only)
        if (isSingleDocument) {
          for (const tagId of currentTagIds) {
            if (!selectedTagIds.has(tagId)) {
              await removeTagFromDocument(docId, tagId)
            }
          }
        }
      }
      onOpenChange(false)
    } catch {
      // Error handled in store
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isSingleDocument
              ? `Manage Tags for "${documentName}"`
              : `Add Tags to ${documentIds.length} Documents`}
          </DialogTitle>
          <DialogDescription>
            {isSingleDocument
              ? 'Select or create tags for this document.'
              : 'Select tags to add to the selected documents.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Existing Tags */}
          <div className="space-y-2">
            <Label>Available Tags</Label>
            {tags.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No tags yet. Create one below.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const isSelected = selectedTagIds.has(tag.id)
                  return (
                    <Badge
                      key={tag.id}
                      variant={isSelected ? 'default' : 'outline'}
                      className={cn(
                        'cursor-pointer transition-colors',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      )}
                      onClick={() => toggleTag(tag.id)}
                    >
                      {isSelected && <Check className="h-3 w-3 mr-1" />}
                      {tag.name}
                    </Badge>
                  )
                })}
              </div>
            )}
          </div>

          {/* Create New Tag */}
          <div className="space-y-2">
            <Label>Create New Tag</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Tag name..."
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTagName.trim()) {
                    e.preventDefault()
                    void handleCreateTag()
                  }
                }}
                disabled={isCreating}
              />
              <Button
                variant="outline"
                onClick={() => void handleCreateTag()}
                disabled={!newTagName.trim() || isCreating}
              >
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Selected tags summary */}
          {selectedTagIds.size > 0 && (
            <div className="pt-2 border-t">
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Tag className="h-3 w-3" />
                {selectedTagIds.size} tag{selectedTagIds.size !== 1 ? 's' : ''}{' '}
                will be {isSingleDocument ? 'set' : 'added'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleApply()} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isSingleDocument ? 'Save' : 'Apply Tags'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
